// src/pages/Layout.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Announcement as AnnouncementIcon,
  Article as ArticleIcon,
  BarChart as BarChartIcon,
  Cake as CakeIcon,
  CalendarMonth as CalendarMonthIcon,
  Chat as ChatIcon,
  Class as ClassIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Contacts as ContactsIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  DoneAll as DoneAllIcon,
  Event as EventIcon,
  FolderSpecial as FolderSpecialIcon,
  Group as GroupIcon,
  Groups as GroupsIcon,
  HowToReg as HowToRegIcon,
  Info as InfoIcon,
  Menu as MenuIcon,
  NotificationsActive as NotificationsActiveIcon,
  Payment as PaymentIcon,
  People as PeopleIcon,
  Public as PublicIcon,
  School as SchoolIcon,
  ShoppingBag as ShoppingBagIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import mkdLogo from '../assets/images/MKD-blanc.png';

const DRAWER_WIDTH = 270;
const COLLAPSED_DRAWER_WIDTH = 76;
const SIDEBAR_STORAGE_KEY = 'mkd-admin-sidebar-open';
const PAGE_USAGE_STORAGE_KEY = 'mkd-admin-page-usage';

const NAV_GROUPS = [
  {
    title: 'Principal',
    items: [
      { label: 'Tableau de bord', path: '/dashboard', icon: <DashboardIcon /> },
      { label: 'Planning', path: '/planning', icon: <CalendarMonthIcon /> },
    ],
  },
  {
    title: 'Utilisateurs',
    items: [
      { label: 'Utilisateurs', path: '/users', icon: <GroupIcon /> },
      { label: 'Professeurs', path: '/teachers', icon: <SchoolIcon /> },
      { label: 'Élèves', path: '/students', icon: <PeopleIcon /> },
      { label: 'Parents', path: '/parents', icon: <GroupsIcon /> },
      { label: 'Présences', path: '/attendance', icon: <HowToRegIcon /> },
    ],
  },
  {
    title: 'Cours & événements',
    items: [
      { label: 'Cours', path: '/courses', icon: <ClassIcon /> },
      { label: 'Événements', path: '/events', icon: <EventIcon /> },
      { label: 'Inscriptions', path: '/registrations', icon: <HowToRegIcon /> },
      { label: 'Auditions Crew', path: '/crew-auditions', icon: <GroupsIcon /> },
    ],
  },
  {
    title: 'Contenu',
    items: [
      { label: 'Annonces', path: '/announcements', icon: <AnnouncementIcon /> },
      { label: 'Blog', path: '/website/blog', icon: <ArticleIcon /> },
      { label: 'Ressources', path: '/resources', icon: <FolderSpecialIcon /> },
      { label: 'Gestion du site', path: '/website', icon: <PublicIcon /> },
    ],
  },
  {
    title: 'Boutique & administration',
    items: [
      { label: 'Boutique', path: '/shop', icon: <ShoppingCartIcon /> },
      { label: 'Commandes', path: '/shop/orders', icon: <ShoppingBagIcon /> },
      { label: 'Contacts', path: '/contacts', icon: <ContactsIcon /> },
      { label: 'Devis', path: '/quotes', icon: <DescriptionIcon /> },
      { label: 'Paiements', path: '/payments', icon: <PaymentIcon /> },
      { label: 'Comptabilité', path: '/accounting', icon: <AccountBalanceIcon /> },
      { label: 'Documents', path: '/documents', icon: <FolderSpecialIcon /> },
    ],
  },
  {
    title: 'Autres',
    items: [
      { label: 'Objectifs', path: '/goals', icon: <BarChartIcon /> },
      { label: 'Chats', path: '/chats', icon: <ChatIcon /> },
      { label: 'Notifications', path: '/notifications', icon: <NotificationsActiveIcon /> },
      { label: 'Statistiques', path: '/stats', icon: <BarChartIcon /> },
    ],
  },
];

const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((group) => group.items);

function normalizeTrackedPath(pathname) {
  if (!pathname) return '/';

  const sortedItems = [...ALL_NAV_ITEMS].sort(
    (a, b) => b.path.length - a.path.length
  );

  const matchingItem = sortedItems.find((item) => {
    if (item.path === '/') return pathname === '/';
    return pathname === item.path || pathname.startsWith(`${item.path}/`);
  });

  return matchingItem?.path || pathname;
}

function readPageUsage() {
  try {
    const raw = localStorage.getItem(PAGE_USAGE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function savePageUsage(usage) {
  try {
    localStorage.setItem(PAGE_USAGE_STORAGE_KEY, JSON.stringify(usage));
  } catch {
    // localStorage peut être indisponible dans certains environnements.
  }
}

function getNotificationIcon(type) {
  switch (type) {
    case 'product_order':
      return <ShoppingBagIcon fontSize="small" />;
    case 'registration':
      return <HowToRegIcon fontSize="small" />;
    case 'birthday':
      return <CakeIcon fontSize="small" />;
    default:
      return <InfoIcon fontSize="small" />;
  }
}

function formatNotificationDate(value) {
  try {
    const date = value?.toDate?.() || (value ? new Date(value) : null);
    if (!date || Number.isNaN(date.getTime())) return '';

    return date.toLocaleString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
}

function NotificationBell() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [highlightedUnreadIds, setHighlightedUnreadIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => notification.read !== true).length,
    [notifications]
  );

  useEffect(() => {
    const notificationsQuery = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        setNotifications(
          snapshot.docs.map((document) => ({
            id: document.id,
            ...document.data()
          }))
        );
        setLoading(false);
      },
      (error) => {
        console.error('Erreur chargement notifications:', error);
        setNotifications([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const markAllVisibleAsRead = async (items = notifications) => {
    const unread = items.filter((notification) => notification.read !== true);
    if (!unread.length) return;

    try {
      const batch = writeBatch(db);

      unread.forEach((notification) => {
        batch.update(doc(db, 'notifications', notification.id), {
          read: true,
          readAt: serverTimestamp()
        });
      });

      await batch.commit();

      setNotifications((previous) =>
        previous.map((notification) => ({
          ...notification,
          read: true
        }))
      );
    } catch (error) {
      console.error('Erreur marquage notifications lues:', error);
    }
  };

  const handleOpen = async (event) => {
    const unreadIds = notifications
      .filter((notification) => notification.read !== true)
      .map((notification) => notification.id);

    setHighlightedUnreadIds(unreadIds);
    setAnchorEl(event.currentTarget);
    await markAllVisibleAsRead();
  };

  const handleClose = () => {
    setAnchorEl(null);
    setHighlightedUnreadIds([]);
  };

  const goToNotifications = () => {
    handleClose();
    navigate('/notifications');
  };

  const goToNotificationTarget = async (notification) => {
    try {
      if (notification.read !== true) {
        await updateDoc(doc(db, 'notifications', notification.id), {
          read: true,
          readAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Erreur notification read:', error);
    }

    handleClose();

    if (notification.link) {
      navigate(notification.link);
      return;
    }

    if (notification.type === 'product_order') {
      navigate('/shop/orders');
    } else if (notification.type === 'registration') {
      navigate('/registrations');
    } else if (notification.type === 'birthday') {
      navigate('/students');
    } else {
      navigate('/notifications');
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          sx={{
            width: 42,
            height: 42,
            border: '1px solid rgba(148, 163, 184, 0.45)',
            borderRadius: 2.5,
            bgcolor: '#FFFFFF',
            color: '#111827',
            '&:hover': { bgcolor: '#F8FAFC' }
          }}
        >
          <Badge badgeContent={unreadCount} color="error" invisible={!unreadCount}>
            <NotificationsActiveIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 410,
            maxWidth: 'calc(100vw - 32px)',
            mt: 1,
            borderRadius: 3,
            boxShadow: '0 18px 50px rgba(15, 23, 42, 0.22)',
            overflow: 'hidden'
          }
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Notifications
          </Typography>

          <Button
            size="small"
            startIcon={<DoneAllIcon />}
            onClick={() => markAllVisibleAsRead()}
            disabled={!notifications.some((notification) => notification.read !== true)}
          >
            Tout lu
          </Button>
        </Box>

        <Divider />

        {loading ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Chargement...
            </Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Aucune notification pour le moment.
            </Typography>
          </Box>
        ) : (
          notifications.slice(0, 7).map((notification, index) => {
            const wasUnreadOnOpen = highlightedUnreadIds.includes(notification.id);
            const isNew = notification.read !== true || wasUnreadOnOpen;

            return (
              <Box key={notification.id}>
                <MenuItem
                  onClick={() => goToNotificationTarget(notification)}
                  sx={{
                    alignItems: 'flex-start',
                    gap: 1.5,
                    py: 1.55,
                    bgcolor: isNew ? '#F5F3FF' : '#FFFFFF',
                    borderLeft: isNew
                      ? '5px solid #7C3AED'
                      : '5px solid transparent',
                    '&:hover': {
                      bgcolor: isNew ? '#EDE9FE' : '#F8FAFC'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: isNew ? '#7C3AED' : '#CBD5E1',
                        color: '#FFFFFF'
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: isNew ? 800 : 600 }}
                      >
                        {notification.title || 'Notification'}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            color: '#64748B',
                            display: 'block',
                            whiteSpace: 'normal',
                            lineHeight: 1.35,
                            mt: 0.2
                          }}
                        >
                          {notification.message || ''}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            color: '#94A3B8',
                            display: 'block',
                            mt: 0.4
                          }}
                        >
                          {formatNotificationDate(notification.createdAt)}
                        </Typography>
                      </>
                    }
                  />
                </MenuItem>

                {index < Math.min(notifications.length, 7) - 1 && <Divider />}
              </Box>
            );
          })
        )}

        <Divider />

        <Box sx={{ p: 1 }}>
          <Button fullWidth variant="outlined" onClick={goToNotifications}>
            Voir toutes les notifications
          </Button>
        </Box>
      </Menu>
    </>
  );
}

function SidebarContent({ currentPath, onNavigate, onClose, collapsed = false }) {
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#111827',
        color: '#FFFFFF',
        overflowX: 'hidden'
      }}
    >
      <Box
        sx={{
          px: collapsed ? 1 : 2.5,
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        <Box
          component="img"
          src={mkdLogo}
          alt="MKD"
          sx={{
            display: 'block',
            width: collapsed ? 44 : 112,
            maxHeight: collapsed ? 44 : 48,
            objectFit: 'contain',
            transition: theme => theme.transitions.create('width', {
              duration: theme.transitions.duration.shorter
            })
          }}
        />

        {onClose && !collapsed && (
          <IconButton onClick={onClose} sx={{ color: '#FFFFFF' }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1.5 }}>
        {NAV_GROUPS.map((group, groupIndex) => (
          <Box key={group.title} sx={{ mb: collapsed ? 0.5 : 1.5 }}>
            {!collapsed && (
              <Typography
                variant="overline"
                sx={{
                  display: 'block', px: 2.5, pb: 0.5,
                  color: '#94A3B8', fontWeight: 800, letterSpacing: 0.8
                }}
              >
                {group.title}
              </Typography>
            )}

            <List disablePadding>
              {group.items.map((item) => {
                const selected =
                  currentPath === item.path || currentPath.startsWith(`${item.path}/`);

                const button = (
                  <ListItemButton
                    key={item.path}
                    selected={selected}
                    onClick={() => {
                      onNavigate(item.path);
                      onClose?.();
                    }}
                    sx={{
                      mx: collapsed ? 1 : 1.25,
                      my: 0.25,
                      minHeight: 44,
                      px: collapsed ? 1.25 : 2,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      borderRadius: 2,
                      color: selected ? '#FFFFFF' : '#CBD5E1',
                      bgcolor: selected ? '#7C3AED' : 'transparent',
                      '&.Mui-selected': { bgcolor: '#7C3AED' },
                      '&.Mui-selected:hover': { bgcolor: '#6D28D9' },
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: '#FFFFFF' }
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed ? 0 : 38,
                        justifyContent: 'center',
                        color: selected ? '#FFFFFF' : '#94A3B8'
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!collapsed && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: 14, fontWeight: selected ? 800 : 600 }}
                      />
                    )}
                  </ListItemButton>
                );

                return collapsed ? (
                  <Tooltip key={item.path} title={item.label} placement="right">
                    {button}
                  </Tooltip>
                ) : button;
              })}
            </List>

            {!collapsed && groupIndex < NAV_GROUPS.length - 1 && (
              <Divider sx={{ mt: 1.5, mx: 2, borderColor: 'rgba(255,255,255,0.08)' }} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function Layout({ children }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  const [pageUsage, setPageUsage] = useState(() => readPageUsage());
  const lastTrackedPath = useRef('');

  const desktopDrawerWidth = desktopOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH;

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(desktopOpen));
    } catch {
      // Le stockage local peut être indisponible.
    }
  }, [desktopOpen]);

  useEffect(() => {
    const normalizedPath = normalizeTrackedPath(location.pathname);
    if (lastTrackedPath.current === normalizedPath) return;
    lastTrackedPath.current = normalizedPath;

    setPageUsage((previous) => {
      const next = {
        ...previous,
        [normalizedPath]: (Number(previous[normalizedPath]) || 0) + 1
      };
      savePageUsage(next);
      return next;
    });
  }, [location.pathname]);

  const topPages = useMemo(() => {
    return ALL_NAV_ITEMS
      .map((item) => ({ ...item, visits: Number(pageUsage[item.path]) || 0 }))
      .filter((item) => item.visits > 0)
      .sort((a, b) => b.visits - a.visits || a.label.localeCompare(b.label, 'fr'))
      .slice(0, 5);
  }, [pageUsage]);

  const visibleTopPages = topPages.length > 0
    ? topPages
    : ALL_NAV_ITEMS.slice(0, 5).map((item) => ({ ...item, visits: 0 }));

  const handleNavigate = (path) => navigate(path);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
          ml: { lg: `${desktopDrawerWidth}px` },
          width: { lg: `calc(100% - ${desktopDrawerWidth}px)` },
          bgcolor: 'rgba(255,255,255,0.96)',
          color: '#111827',
          borderBottom: '1px solid #E5E7EB',
          backdropFilter: 'blur(12px)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter
          })
        }}
      >
        <Toolbar sx={{ minHeight: 72, gap: 1.5 }}>
          <Tooltip title={isDesktop ? (desktopOpen ? 'Réduire la barre latérale' : 'Afficher la barre latérale') : 'Ouvrir le menu'}>
            <IconButton
              edge="start"
              onClick={() => isDesktop ? setDesktopOpen((value) => !value) : setMobileOpen(true)}
              aria-label={isDesktop ? 'Afficher ou réduire la barre latérale' : 'Ouvrir le menu'}
            >
              {isDesktop ? (desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />) : <MenuIcon />}
            </IconButton>
          </Tooltip>

          <Typography variant="subtitle1" fontWeight={900} sx={{ display: { xs: 'none', md: 'block' }, mr: 1 }}>
            Pages les plus consultées
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, overflowX: 'auto', py: 0.5 }}>
            {visibleTopPages.map((item) => {
              const active = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
              return (
                <Button
                  key={item.path}
                  size="small"
                  variant={active ? 'contained' : 'outlined'}
                  startIcon={item.icon}
                  onClick={() => handleNavigate(item.path)}
                  sx={{ flexShrink: 0, minWidth: 0, textTransform: 'none', borderRadius: 2, fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>

          <NotificationBell />
        </Toolbar>
      </AppBar>

      <Box component="nav" aria-label="Navigation principale">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, border: 0 }
          }}
        >
          <SidebarContent
            currentPath={location.pathname}
            onNavigate={handleNavigate}
            onClose={() => setMobileOpen(false)}
          />
        </Drawer>

        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': {
              width: desktopDrawerWidth,
              border: 0,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter
              })
            }
          }}
        >
          <SidebarContent
            currentPath={location.pathname}
            onNavigate={handleNavigate}
            collapsed={!desktopOpen}
          />
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          ml: { lg: `${desktopDrawerWidth}px` },
          pt: '72px',
          minHeight: '100vh',
          width: { xs: '100%', lg: `calc(100% - ${desktopDrawerWidth}px)` },
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter
          })
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default Layout;
